// Service History logic shared by today's tab (ServiceHistoryTab) and the
// full unit page (UnitV2/tabs/ServiceTabV2), so the two cannot drift.

export const fmtServiceDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return iso;
    }
};

export const fmtServiceDateShort = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    } catch {
        return iso;
    }
};

/** "$1,234" for a part's list price; null for none / zero. */
export const fmtPartPrice = (val) => {
    if (!val || val === 0) return null;
    return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

/** A one-liner from the first complaint (or correction), job prefix stripped. */
export const workOrderOneLiner = (wo) => {
    if (!wo.ccc || wo.ccc.length === 0) return 'No description';
    const first = wo.ccc[0].complaint || wo.ccc[0].correction || '';
    const firstLine = first.split(/\r?\n/).find((l) => l.trim()) || '';
    return firstLine.replace(/^\d{2}\s+/, '').trim() || 'Work order';
};

/** Newest first; work orders without a date last. */
export const sortWorkOrders = (workOrders) => [...(workOrders || [])].sort((a, b) => {
    if (!a.closed_date) return 1;
    if (!b.closed_date) return -1;
    return new Date(b.closed_date) - new Date(a.closed_date);
});

/** "3 parts" / "1 part", or null without parts. */
export const partsPillText = (wo) => {
    const n = wo.parts ? wo.parts.length : 0;
    return n > 0 ? `${n} part${n !== 1 ? 's' : ''}` : null;
};

/** Parts from the sales-order planning table, not fitted yet. */
export const isPlannedParts = (wo) => wo.parts_source === 'InSalPar';

/** The Complaint / Cause / Correction lines of one job, in that order. */
export const cccLines = (item) => [
    ['complaint', 'Complaint', item.complaint],
    ['cause', 'Cause', item.cause],
    ['correction', 'Correction', item.correction],
].filter(([, , text]) => text).map(([kind, label, text]) => ({ kind, label, text: text.trim() }));

// ── Pre-call brief ──────────────────────────────────────────────────────────

export const BRIEF_SECTIONS = Object.freeze({
    'CONDITION': { label: 'Condition', cls: 'svc2-section--condition' },
    'IN 30 SECONDS': { label: 'In 30 seconds', cls: 'svc2-section--brief' },
    'WHAT YOU CAN SAY': { label: 'What you can say', cls: 'svc2-section--say' },
    'WATCH OUT': { label: 'Watch out', cls: 'svc2-section--watch' },
    'MAINTENANCE PATTERN': { label: 'Maintenance pattern', cls: 'svc2-section--pattern' },
});

/**
 * The brief's sections, always in the order Condition, In 30 seconds, What you
 * can say, Watch out, Maintenance pattern. Each { heading, content, lines,
 * bullets } — bullets when every line starts with •. Without any known
 * heading, the raw text as one section (heading null).
 */
export function parseBrief(text) {
    const headings = Object.keys(BRIEF_SECTIONS);
    const sections = [];
    const remaining = (text || '').trim();
    for (let i = 0; i < headings.length; i += 1) {
        const heading = headings[i];
        const nextHeading = headings[i + 1];
        const start = remaining.indexOf(heading);
        if (start !== -1) {
            const contentStart = start + heading.length;
            const end = nextHeading ? remaining.indexOf(nextHeading, contentStart) : remaining.length;
            const content = remaining.slice(contentStart, end !== -1 ? end : undefined).trim();
            sections.push({ heading, content });
        }
    }
    const out = sections.length ? sections : [{ heading: null, content: remaining }];
    return out.map((s) => {
        const lines = s.content.split('\n').filter((l) => l.trim());
        const bullets = lines.every((l) => l.trim().startsWith('•'));
        return { ...s, lines: bullets ? lines.map((l) => l.trim().replace(/^•\s*/, '')) : lines, bullets };
    });
}

/** The unit fields the brief and the follow-up chat send. */
export const serviceUnitMeta = (unit, mfOptions) => (unit ? {
    make: unit.MAKE,
    model: unit.MODEL,
    category: unit.CATEGORY,
    year: unit.YEAR_MANUF,
    hours: unit.ODOMETER,
    price: unit.Best_Sell_Price || unit.MMYMedianAskingPrice,
    days_in_stock: unit.DaysInStock,
    new_used: unit.NewUsedStatus,
    ml_predicted_price: unit.MLPrediction > 0 ? unit.MLPrediction : null,
    predicted_price: unit.PredictedPrice > 0 ? unit.PredictedPrice : null,
    days_to_sell: unit.DaysToSellPrediction,
    days_to_sell_category: unit.DaysToSellCategory,
    predicted_bucket: unit.PredictedBucket,
    bucket_confidence: unit.BucketConfidence,
    internal_price: unit.InternalPrice,
    gross_margin_pct: unit.GrossMarginPct,
    margin_after_carrying_pct: unit.MarginAfterCarryingPct,
    mf_options: mfOptions && mfOptions.length > 0 ? mfOptions : undefined,
} : {});

// The thinking loader's steps (today's wording and timing).
export const SERVICE_THINKING_STEPS = Object.freeze([
    { label: 'Fetching workshop repair orders', detail: 'Work orders, dates, costs' },
    { label: 'Parsing complaint / cause / correction', detail: 'CCC breakdown per job' },
    { label: 'Matching parts and labour', detail: 'Parts used, technician hours' },
    { label: 'Building service timeline', detail: 'Chronological history view' },
]);
export const SERVICE_STEP_DURATIONS = Object.freeze([1200, 1000, 1000, 99999]);

// A failed brief never shows the raw transport error ("Network Error"); the
// user gets a plain sentence and a Retry (QA P-3).
export const BRIEF_ERROR = 'The pre-call brief could not be prepared right now. Please try again.';
